"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { capture, isPostHogEnabled, registerPostHog, resolveAnalyticsPlanType } from "@/lib/posthog-browser";
import { acquireInitSlot, releaseInitSlot } from "@/lib/posthog-init-guard";

/**
 * PostHog client-side init + pageview tracker.
 *
 * RENDERS NOTHING AND WRAPS NOTHING. It is mounted as a SIBLING of the page in
 * `app/layout.tsx`, never as an ancestor of it, and that structure is the whole
 * point (#2401). This component calls `useSearchParams`, and on a prerendered
 * route Next renders the client tree *up to the closest `Suspense` boundary* on
 * the client instead of at build time — see the "Prerendering" section of
 * `node_modules/next/dist/docs/01-app/03-api-reference/04-functions/use-search-params.md`.
 *
 * While this lived in a `PostHogProvider` that wrapped `{children}` inside
 * `<Suspense>`, that boundary *was the entire page*, so every prebuilt public
 * page shipped HTML containing the nav and nothing else: no `<h1>`, none of the
 * page's own `application/ld+json`, a `BAILOUT_TO_CLIENT_SIDE_RENDERING` marker,
 * and content that only appeared once JS ran. AI answer crawlers mostly do not
 * run JS, so they saw a nav bar where the provider facts should be. The old
 * wrapper provided no React context — it returned `<>{children}</>` — so
 * nothing ever needed it to be an ancestor.
 *
 * KEEP IT CHILDLESS. Giving this a `children` prop again, or rendering it
 * anywhere that makes it an ancestor of the page, restores the bailout on every
 * prerendered route. `tests/contracts/prerender-search-params-bailout-contract.test.ts`
 * fails the build if that happens, and `scripts/ci/check-prerendered-content.ts`
 * fails the `next build` lane if the marker reappears in built HTML.
 *
 * Initializes the PostHog browser SDK once on mount. Gated on the
 * presence of NEXT_PUBLIC_POSTHOG_KEY — missing key means no init and
 * no events (no-op), so local dev and PR previews don't spam a project.
 *
 * Also fires a `$pageview` event on every App Router navigation.
 * PostHog's browser SDK autocaptures $pageview on initial load, but
 * the App Router's client-side nav is invisible to it — we observe
 * pathname/searchParams changes and `capture` manually.
 *
 * Events go through `lib/posthog-browser`, which owns the SDK handle and
 * queues anything captured before the deferred init finishes. This component
 * no longer keeps a readiness flag or a pending-URL slot of its own: holding
 * two readiness signals a remote-config round trip apart is what made a stale
 * URL replay AFTER a newer pageview, and a one-slot queue is what dropped the
 * landing pageview when a navigation beat the idle callback (#2413).
 *
 * IMPORTANT — privacy (Week 1 Sprint acceptance #8): no PII in any
 * event payload. The autocapture config is explicit about what we
 * collect, and `person_profiles: 'identified_only'` means anonymous
 * visitors don't get a PostHog person record until we explicitly
 * identify them (which we will only do post-signup, with the parent's
 * Supabase user id — never their email or name).
 *
 * Perf (§14): `posthog-js` is ~60 kB minified + gzipped and is pure
 * analytics — it must never compete with LCP. The SDK is dynamically
 * imported inside the mount effect so it ships in its own lazy chunk,
 * loaded after hydration. The type-only import above keeps tsc happy
 * without emitting any runtime code for the library.
 *
 * See FOLLOWUPS.md #4 for the steps to create the PostHog project
 * and wire the key.
 */
// Guard against a second init if this ever mounts twice (React strict mode,
// a remount). The SDK handle itself lives in lib/posthog-browser; the slot is
// in lib/posthog-init-guard, which is where the release rule is tested.

export function PostHogPageview() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Init once on mount — dynamic import so posthog-js doesn't ship
  // in the main bundle. Further deferred to `requestIdleCallback` so
  // the chunk isn't even fetched until the browser is idle
  // post-LCP: analytics must never compete with first paint (§14).
  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;

    if (!key) {
      return;
    }
    if (!acquireInitSlot()) {
      return;
    }

    let cancelled = false;
    // Whether `posthog.init()` has actually been CALLED — not whether it was
    // scheduled. This is what makes the guard above releasable on cleanup; see
    // the cleanup note below.
    let sdkInitCalled = false;

    const init = () => {
      if (cancelled) return;
      void import("posthog-js").then(({ default: posthog }) => {
        if (cancelled) return;
        sdkInitCalled = true;
        posthog.init(key, {
          api_host: host ?? "https://us.i.posthog.com",
          person_profiles: "identified_only",
          capture_pageview: false, // manual pageview below covers client nav
          capture_pageleave: true,
          autocapture: false, // explicit events only, no DOM autocapture
          disable_session_recording: true,
          capture_exceptions: true,
          loaded: async (ph) => {
            // Hand the SDK over and flush anything captured while the
            // deferred init was in flight. This is the ONE readiness signal
            // (#2413): the old code set a module handle when init() returned
            // and a separate `posthogReady` state here, one remote-config
            // round trip apart, and that skew replayed a stale URL after a
            // newer pageview had already been sent.
            registerPostHog(ph);
            if (process.env.NODE_ENV === "development") {
              ph.debug(false);
            }
            // Identify the logged-in user so client-side events are
            // correlated with the server-side events in lib/analytics.ts.
            // We use the Supabase auth user id — never email or name (§13).
            try {
              const { getSupabaseBrowserClient } = await import(
                "@/lib/supabase-browser"
              );
              const supabase = getSupabaseBrowserClient();
              const auth = supabase.auth as {
                getUser: () => Promise<{
                  data: { user: { id?: string | null } | null };
                }>;
              };
              const {
                data: { user },
              } = await auth.getUser();
              if (user?.id) {
                const planType = await resolveAnalyticsPlanType(supabase, user.id);
                ph.identify(user.id, { plan_type: planType });
              }
            } catch (err) {
              // Non-critical — analytics must never break the page.
              console.error("[posthog-pageview] identify failed", err);
            }
          },
        });
      });
    };

    const ric = (
      window as unknown as {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number },
        ) => number;
        cancelIdleCallback?: (id: number) => void;
      }
    ).requestIdleCallback;
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    if (typeof ric === "function") {
      idleId = ric(init, { timeout: 3000 });
    } else {
      timeoutId = window.setTimeout(init, 1500);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined) {
        (
          window as unknown as { cancelIdleCallback?: (id: number) => void }
        ).cancelIdleCallback?.(idleId);
      }
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);

      // RELEASE THE MODULE GUARD IF THE INIT NEVER ACTUALLY HAPPENED (PR review).
      //
      // `initStarted` is module state, so it outlives this component. Setting it
      // on SCHEDULE and never clearing it made a cancelled schedule permanent:
      // React Strict Mode mounts, runs this cleanup, and mounts again, and the
      // second effect returned early on a flag left behind by a callback that
      // was cancelled before it ran. `registerPostHog` was then never called, so
      // every event queued forever and NOTHING reached PostHog — in exactly the
      // environment where you would go to verify that this PR works. A real
      // unmount/remount before the idle callback fires does the same thing in
      // production.
      //
      // Releasing is safe up to the moment `posthog.init()` is called, and no
      // further: the `cancelled` check inside the import's `.then()` runs BEFORE
      // that call, so a teardown mid-import leaves the SDK untouched and a
      // retry is correct. Once init has been called, keep the guard — that is
      // the double-init this flag exists to prevent.
      if (!sdkInitCalled) releaseInitSlot();
    };
  }, []);

  // Manual $pageview on path/search change. `capture` sends immediately when
  // the SDK is ready and otherwise queues IN ORDER, so a navigation that
  // happens before the deferred init finishes no longer overwrites the
  // landing pageview — both are delivered, oldest first (#2413).
  useEffect(() => {
    if (!isPostHogEnabled()) {
      return;
    }
    if (!pathname) {
      return;
    }
    const url = searchParams?.toString()
      ? `${pathname}?${searchParams.toString()}`
      : pathname;
    capture("$pageview", { $current_url: url });
  }, [pathname, searchParams]);

  // Nothing to render: the page is a sibling, not a child (#2401).
  return null;
}
